import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  Cpu, Database, Network, CircleDot, ShieldCheck, Lock,
  CheckCircle2, ChevronRight, Sparkles, FileDown, ArrowRight,
  Droplets, Mountain, Factory, Calendar, Clock, Target,
} from "lucide-react";
import usePageview, { getVisitorSession } from "@/lib/usePageview";

// Phase 25.13ag — Industrial Connector design-partner landing page.
//
// Distinct from /industrial (broad credibility page) — this URL is the
// outreach destination for cold LinkedIn DMs to mining-services, water,
// and food-processing operators. Frames a 12-week zero-fee pilot with a
// dedicated capture form and a downloadable 1-page brief.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROTOCOLS = [
  { name: "OPC UA", coverage: "MVP", Icon: Network, color: "#FFB000", note: "Native via asyncua. Siemens · Rockwell · GE · ABB." },
  { name: "OSIsoft PI Web API", coverage: "MVP", Icon: Database, color: "#FFB000", note: "REST · 30d backfill · incremental sync." },
  { name: "MQTT / Sparkplug B", coverage: "MVP", Icon: CircleDot, color: "#FFB000", note: "Mosquitto / HiveMQ subscriber · QoS-aware." },
  { name: "MODBUS TCP / RTU", coverage: "Q3 2026", Icon: CircleDot, color: "#71717A", note: "pymodbus · serial-over-RS485 + TCP." },
  { name: "Aveva InTouch / Wonderware", coverage: "Q3 2026", Icon: Database, color: "#71717A", note: "Tag export via OPC UA bridge." },
  { name: "Ignition (Inductive Automation)", coverage: "Q4 2026", Icon: Database, color: "#52525B", note: "Native module via Ignition Gateway." },
];

const PILOT_TERMS = [
  { label: "Duration", value: "12 calendar weeks · weekly check-ins · final review at Week 12" },
  { label: "DARA provides", value: "Read-only connector for OPC UA / PI / MQTT · DARA workspace · onboarding · dedicated CS · 4 AI-grounded operational briefs per week" },
  { label: "You provide", value: "Read-only access to one tag-set · weekly 30-min check-in · honest feedback" },
  { label: "Cost to you", value: "$0 AUD. No license fee. No software fee. No infra cost. DARA absorbs cloud + cloud + inference costs for the pilot.", highlight: true },
  { label: "IP + data", value: "DARA retains 100% of platform IP. Your operational data stays yours. We never re-use, never resell, never train models on it. Mutual NDA pre-pilot." },
  { label: "Success path", value: "If pilot generates ≥1 verified avoided-incident OR ≥5% OEE improvement → commercial discussion (target: $3-8k/site/month). Non-exclusive." },
  { label: "Exit clause", value: "Either party may exit with 14-day written notice. Data already captured is deleted on your request within 30 days." },
];

const DELIVERABLES = [
  { week: "Week 4", title: "Connector live", body: "Read-only OPC UA / PI tag-set live in your DARA workspace. InfoSec sign-off complete." },
  { week: "Week 6", title: "Operations dashboard", body: "Bloomberg-terminal-style live dashboard with your tag streams + KPI rollups, accessible to your Manager-OT and plant engineers." },
  { week: "Week 8", title: "Weekly DARA brief", body: "DARA-grounded weekly operational brief — vibration drift, throughput anomalies, OEE leaks with severity grading." },
  { week: "Week 10", title: "Predictive alerts", body: "ISO 10816 vibration bands, pump degradation curves, motor-current signatures → email + Slack alerts before failure." },
  { week: "Week 12", title: "Commercial proposal", body: "Written 'what would have to be true' proposal with site-level pricing + roll-out plan for the next 30/90/365 days." },
];

const TARGET_SECTORS = [
  {
    Icon: Mountain,
    label: "Mining Services",
    sub: "BHP / Rio / Fortescue contractors · OSIsoft PI plants · 1% grade reconciliation = $4M/year",
  },
  {
    Icon: Droplets,
    label: "Water Utilities",
    sub: "SA Water · Sydney Water · Water Corp WA · 2027 regulator mandate for digital-twin pathway",
  },
  {
    Icon: Factory,
    label: "Food Processing",
    sub: "Inghams · Lion · Mondelez · JBS · HACCP-grade lineage + plant-level OEE on existing SCADA",
  },
];

const FAQ = [
  {
    q: "Do you need to touch our PLCs?",
    a: "No. The pilot is strictly read-only. We talk to your historian or OPC UA server. We do not write back. We do not initiate control actions. We do not connect to PLCs.",
  },
  {
    q: "Do you need new hardware on site?",
    a: "No. For the pilot, we connect over your existing VPN or site-to-site link (IPsec or WireGuard) to a jump-host in your DMZ. Reference hardware (Advantech, Moxa) is on the roadmap for sites without existing OT infrastructure — but not required for the design-partner pilot.",
  },
  {
    q: "What's the InfoSec story?",
    a: "Per-session rotating tokens · full audit log shipped to your SIEM (Splunk / Sentinel / Elastic compatible) · IEC 62443 Level 2 alignment roadmap · OT-security partner sign-off available on request (Penten, Claroty AU partner — subject to NDA). SOC 2 Type II in progress, target Q1 2027.",
  },
  {
    q: "Why is the pilot free?",
    a: "Because we need your honest feedback more than your money. The first 2 design partners shape the protocol roadmap, the alert taxonomy, and the commercial offering. Your input is worth more than license revenue at this stage. After the pilot, target pricing is $3-8k/site/month depending on tag count and protocol mix.",
  },
  {
    q: "How long is the sales cycle? We can't promise a commercial close.",
    a: "We're explicitly NOT asking for a commercial close. We're asking for 12 weeks of read-only access in exchange for $0 and 4 deliverables. If at Week 12 we haven't generated either an avoided incident or measurable OEE gain, we walk away clean.",
  },
];

export default function IndustrialConnector() {
  usePageview("/industrial/connector");

  const [form, setForm] = useState({
    email: "", name: "", company: "", role: "", sector: "", protocols: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.company) {
      toast.error("Work email and company are required");
      return;
    }
    setSubmitting(true);
    try {
      // We reuse the existing industry-leads endpoint with a distinct
      // industry slug. Backend already supports arbitrary slugs.
      await axios.post(`${API}/public/industry-leads`, {
        email: form.email,
        name: form.name,
        company: form.company,
        role: form.role,
        message: [
          form.sector ? `Sector: ${form.sector}` : "",
          form.protocols ? `Protocols: ${form.protocols}` : "",
          form.message ? `Notes: ${form.message}` : "",
        ].filter(Boolean).join("\n"),
        industry: "industrial-connector",
        visitor_session: getVisitorSession(),
      });
      setSubmitted(true);
      toast.success("Application received — DARA founder will reach out within 1 business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const briefUrl = `${API}/public/industrial-connector-brief`;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="industrial-connector-page">
      {/* Top nav */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/85 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="back-to-home">
            <span className="text-[#FFB000] font-mono text-sm">{`>_`}</span>
            <span className="font-heading font-black tracking-tight text-white text-base">DARA</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 ml-2 hidden sm:inline">
              · INDUSTRIAL CONNECTOR
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <a
              href={briefUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-download-brief"
              className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000] flex items-center gap-1.5"
            >
              <FileDown className="w-3.5 h-3.5" /> 1-page brief
            </a>
            <a
              href="#apply"
              data-testid="nav-apply"
              className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90"
            >
              Apply
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFB000]/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 relative">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-4">
            // INDUSTRIAL CONNECTOR · DESIGN-PARTNER PROGRAM · 2 SLOTS OPEN
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl">
            An analyst on top of your existing SCADA. <span className="text-[#FFB000]">No new hardware.</span>
          </h1>
          <p className="text-base sm:text-lg text-zinc-300 mt-6 max-w-3xl leading-relaxed">
            60-70% of Australian industrial plants already run a working historian (OSIsoft PI · Aveva · Ignition · Wonderware). They have the data. They don't have an analyst sitting next to it 24/7. DARA's Industrial Connector drops on top — read-only — and turns your tag streams into DARA-grounded operational briefs. Live within 4 weeks. <span className="text-white font-bold">12-week pilot. $0 fee. Two slots open.</span>
          </p>

          <div className="flex flex-wrap gap-2 mt-7" data-testid="hero-meta-pills">
            {["OPC UA", "OSIsoft PI", "MQTT / SPARKPLUG", "READ-ONLY", "IEC 62443 L2", "DARA ENGINE"].map(p => (
              <span key={p} className="px-2.5 py-1 border border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/5 font-mono text-[10px] uppercase tracking-widest">{p}</span>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-4 flex-wrap">
            <a href="#apply" data-testid="hero-cta-apply" className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90">
              <Target className="w-4 h-4" /> Apply as a design partner <ArrowRight className="w-4 h-4" />
            </a>
            <a href={briefUrl} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-pdf" className="font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:text-[#FFB000] underline-offset-4 hover:underline flex items-center gap-1.5">
              <FileDown className="w-3.5 h-3.5" /> Download the 1-page brief
            </a>
          </div>
        </div>
      </section>

      {/* Target sectors */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// WHO WE'RE LOOKING FOR</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          Three sectors. One question: <span className="text-[#FFB000]">do you have a historian we can read?</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900 mt-10 border border-zinc-900" data-testid="target-sectors">
          {TARGET_SECTORS.map(({ Icon, label, sub }) => (
            <div key={label} className="bg-black p-6 sm:p-8">
              <Icon className="w-6 h-6 text-[#FFB000] mb-3" />
              <h3 className="font-heading font-bold text-lg tracking-tight">{label}</h3>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Protocols */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// PROTOCOL COVERAGE</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          Three native protocols at MVP. <span className="text-zinc-500">Modbus and Wonderware next quarter.</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900 mt-10 border border-zinc-900" data-testid="protocols-grid">
          {PROTOCOLS.map(({ name, coverage, Icon, color, note }) => (
            <div key={name} className="bg-black p-6">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4" style={{ color }} />
                  <span className="font-heading font-bold text-base">{name}</span>
                </div>
                <span
                  className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-px border"
                  style={{ color, borderColor: `${color}66` }}
                >
                  {coverage}
                </span>
              </div>
              <p className="text-xs text-zinc-400 leading-relaxed mt-2">{note}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pilot terms */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// PILOT TERMS · 12 WEEKS · ZERO FEE</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          Written down. <span className="text-[#FFB000]">No surprises.</span>
        </h2>
        <div className="mt-10 border border-zinc-900 divide-y divide-zinc-900" data-testid="pilot-terms">
          {PILOT_TERMS.map(({ label, value, highlight }) => (
            <div key={label} className={`grid grid-cols-1 md:grid-cols-[200px_1fr] gap-2 px-4 py-4 ${highlight ? "bg-[#FFB000]/5" : ""}`}>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
              <div className={`text-sm leading-relaxed ${highlight ? "text-[#FFB000] font-bold" : "text-zinc-300"}`}>{value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Deliverables */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// FIVE DELIVERABLES · WEEK BY WEEK</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          By Week 12, you have a Bloomberg-terminal for your plant <span className="text-[#FFB000]">or we walk away clean.</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-zinc-900 mt-10 border border-zinc-900" data-testid="deliverables-grid">
          {DELIVERABLES.map(({ week, title, body }, idx) => (
            <div key={week} className="bg-black p-5">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-3.5 h-3.5 text-[#FFB000]" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">{week}</span>
              </div>
              <h3 className="font-heading font-bold text-sm tracking-tight">{String(idx + 1).padStart(2, "0")} · {title}</h3>
              <p className="text-xs text-zinc-400 leading-relaxed mt-2">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Security */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// SECURITY · ACCESS · COMPLIANCE</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          Designed for an InfoSec review. <span className="text-[#FFB000]">Read-only is non-negotiable.</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900 mt-10 border border-zinc-900" data-testid="security-grid">
          {[
            { Icon: Lock, title: "VPN / Site-to-Site", body: "We connect over your existing IPsec or WireGuard tunnel to a jump-host in your DMZ. Zero connection to PLCs or the control loop." },
            { Icon: ShieldCheck, title: "Per-Session Tokens", body: "Rotating tokens. Full audit log shipped to your SIEM (Splunk / Sentinel / Elastic compatible). IEC 62443 Level 2 alignment roadmap." },
            { Icon: CircleDot, title: "Read-Only Always", body: "We don't write back to your historian. We don't touch PLCs. We don't initiate control actions. Read-only is non-negotiable for the pilot." },
            { Icon: ShieldCheck, title: "OT-Security Sign-Off", body: "Available on request (Penten · Claroty AU partner · subject to NDA). SOC 2 Type II in progress, target Q1 2027. Mutual NDA pre-pilot." },
          ].map(({ Icon, title, body }) => (
            <div key={title} className="bg-black p-6">
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4 text-[#FFB000]" />
                <h3 className="font-heading font-bold text-base">{title}</h3>
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed mt-2">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// FAQ</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">
          The five questions <span className="text-[#FFB000]">every OT lead</span> asks first.
        </h2>
        <div className="mt-10 max-w-4xl divide-y divide-zinc-900 border border-zinc-900" data-testid="faq-list">
          {FAQ.map(({ q, a }, idx) => (
            <div key={idx} className="px-5 py-5">
              <div className="flex items-start gap-3">
                <span className="font-mono text-[10px] text-zinc-700 mt-1">{String(idx + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-heading font-bold text-base text-white">{q}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed mt-2">{a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Apply */}
      <section id="apply" className="border-t border-zinc-900 bg-[#FFB000]/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-3">// APPLY · 2 SLOTS · FIRST CONFIRMED BY DAY 30</div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">
            Apply as a design partner.
          </h2>
          <p className="text-sm text-zinc-400 mt-3 max-w-xl leading-relaxed">
            Drop your details — DARA's founder will reach out within one business day with a 30-min discovery call (no slides). If we're a fit, mutual NDA in 24h and pilot agreement signed by Day 14.
          </p>

          {!submitted && (
            <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="connector-apply-form">
              <Input label="Work email *" testid="apply-email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Input label="Full name" testid="apply-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Input label="Company *" testid="apply-company" required value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
              <Input label="Role / title" testid="apply-role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} />
              <div>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Sector</span>
                  <select
                    value={form.sector}
                    onChange={(e) => setForm({ ...form, sector: e.target.value })}
                    data-testid="apply-sector"
                    className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                  >
                    <option value="">— Select —</option>
                    <option value="mining-services">Mining services</option>
                    <option value="water-utility">Water utility</option>
                    <option value="food-processing">Food processing</option>
                    <option value="energy">Energy / oil + gas</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="epc-consultancy">EPC consultancy (Hatch / Worley / GHD / Aurecon)</option>
                    <option value="other">Other</option>
                  </select>
                </label>
              </div>
              <Input label="Protocols in use (OPC UA, PI, Wonderware, etc.)" testid="apply-protocols" value={form.protocols} onChange={(v) => setForm({ ...form, protocols: v })} />
              <div className="sm:col-span-2">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">One thing you wish your historian could tell you (optional)</span>
                  <textarea
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="apply-message"
                    className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                  />
                </label>
              </div>
              <div className="sm:col-span-2 flex items-center justify-between flex-wrap gap-3 pt-2">
                <p className="font-mono text-[10px] text-zinc-600 max-w-md">
                  By submitting you accept that DARA will contact you about this enquiry. We never share or sell your details. Pre-pilot mutual NDA available on request.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="apply-submit"
                  className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90 disabled:opacity-40"
                >
                  {submitting ? <><Sparkles className="w-3.5 h-3.5 animate-pulse" /> SUBMITTING…</> : <><ChevronRight className="w-3.5 h-3.5" /> Apply as a design partner</>}
                </button>
              </div>
            </form>
          )}

          {submitted && (
            <div className="mt-8 border border-[#00E676]/40 bg-[#00E676]/5 p-6" data-testid="apply-success">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#00E676] mb-2">
                <CheckCircle2 className="w-3.5 h-3.5" />
                APPLICATION RECEIVED
              </div>
              <h3 className="font-heading font-bold text-2xl tracking-tight">You're on the list.</h3>
              <p className="text-sm text-zinc-300 mt-2 max-w-xl leading-relaxed">
                A DARA founder will reach out within one business day with a 30-min discovery slot.
                In the meantime, <a href={briefUrl} target="_blank" rel="noopener noreferrer" className="text-[#FFB000] underline">download the 1-page brief</a> to circulate internally with your Manager-OT or InfoSec lead.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-900 bg-zinc-950/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 sm:grid-cols-4 gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Industries</div>
            <ul className="space-y-2">
              <li><Link to="/industries/mining" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Mining</Link></li>
              <li><Link to="/industries/water" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Water & Utilities</Link></li>
              <li><Link to="/industries/heavy-machinery" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Heavy Machinery</Link></li>
              <li><Link to="/industries/shipping" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Shipping</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Product</div>
            <ul className="space-y-2">
              <li><Link to="/industrial" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Industrial Adapter</Link></li>
              <li><Link to="/founders-guide" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Founder's Guide</Link></li>
              <li><Link to="/security" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Security & Trust</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Account</div>
            <ul className="space-y-2">
              <li><Link to="/login" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Sign in</Link></li>
              <li><Link to="/signup" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Create workspace</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">DARA OS</div>
            <p className="text-[11px] text-zinc-600 leading-relaxed">
              The Bloomberg-terminal for resource and industrial operators. Built in Australia, deployed worldwide.
            </p>
          </div>
        </div>
        <div className="border-t border-zinc-900 px-4 sm:px-6 py-4 max-w-7xl mx-auto flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">© DARA OS · {new Date().getFullYear()}</span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-700">DARA ENGINE · LIVE</span>
        </div>
      </footer>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required = false, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
      />
    </label>
  );
}
